/**
 * Public Layout — GE Lines Self-Service Portal
 *
 * Header + Footer wrapper for all public (pre-login) pages.
 */

import { Outlet, Link, useLocation } from 'react-router-dom';
import { Search, LogIn, Menu, X, Phone, ExternalLink } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useStorefront } from '@/context/StorefrontContext';
import { TenantBrandMark } from '@/components/TenantBrandMark';
import { resolvePortalTheme } from '@/lib/portal-theme';

interface NavLinkItem {
  href: string;
  label: string;
  external?: boolean;
}

function NavLink({ item, className, onClick, children }: { item: NavLinkItem; className: string; onClick?: () => void; children?: ReactNode }) {
  if (item.external) {
    return (
      <a href={item.href} className={className} onClick={onClick}>
        {children ?? item.label}
      </a>
    );
  }

  return (
    <Link to={item.href} className={className} onClick={onClick}>
      {children ?? item.label}
    </Link>
  );
}

export function PublicLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { brand, visual, routes, featureFlags, experienceMode, loginUrl, portalHomeUrl, storefrontBaseUrl } = useStorefront();
  const portalTheme = resolvePortalTheme(brand.portalThemeTemplate, visual);
  const portalLoginEnabled = featureFlags.portalLogin === true;
  const navLinks: NavLinkItem[] = experienceMode === 'portal'
    ? [
        ...(storefrontBaseUrl ? [{ href: storefrontBaseUrl, label: 'Visit Website', external: true }] : []),
        { href: '/contact', label: 'Contact' },
      ]
    : [
        { href: '/', label: 'Home' },
        ...(featureFlags.tracking !== false ? [{ href: '/track', label: 'Track Booking' }] : []),
        ...(featureFlags.bookings !== false ? [{ href: '/book', label: 'Book Shipment' }] : []),
        ...(featureFlags.rates !== false ? [{ href: '/rfq', label: 'Sales Inquiry' }] : []),
        ...(featureFlags.otherInquiry !== false ? [{ href: '/inquiry', label: 'Other Inquiry' }] : []),
        ...(routes.some((route) => route.routePath === '/services') ? [{ href: '/services', label: 'Services' }] : []),
        ...(featureFlags.sailingSchedule || routes.some((route) => route.routePath === '/schedule')
          ? [{ href: '/schedule', label: 'Sailing Schedule' }]
          : []),
        ...(routes.some((route) => route.routePath === '/contact') ? [{ href: '/contact', label: 'Contact' }] : []),
      ];

  return (
    <div className="relative min-h-screen flex flex-col" style={{ ...portalTheme.cssVars, ...portalTheme.shellBackground }}>
      <div className="pointer-events-none absolute inset-0" style={portalTheme.textureOverlay} />
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b border-white/50" style={portalTheme.panelSurface}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="min-w-0">
              <TenantBrandMark
                brand={brand}
                size="md"
                subtitle={brand.portalLabel}
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-7">
              {navLinks.map(link => (
                <NavLink
                  key={link.href}
                  item={link}
                  className={`text-sm font-medium transition-colors ${
                    !link.external && location.pathname === link.href
                      ? 'text-brand-600'
                      : 'text-slate-600 hover:text-brand-600'
                  }`}
                />
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-3">
              {experienceMode !== 'portal' && (
                featureFlags.tracking !== false ? (
                  <Link
                    to="/track"
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:text-brand-600 transition-colors"
                  >
                    <Search className="w-4 h-4" />
                    Track
                  </Link>
                ) : null
              )}
              {portalLoginEnabled && (isAuthenticated ? (
                <NavLink
                  item={{ href: portalHomeUrl, label: 'My Portal', external: portalHomeUrl.startsWith('http') }}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-700 hover:bg-brand-800 rounded-lg transition-colors"
                >
                  My Portal
                </NavLink>
              ) : (
                <NavLink
                  item={{ href: loginUrl, label: 'Sign In', external: loginUrl.startsWith('http') }}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand-700 hover:bg-brand-800 rounded-lg transition-colors"
                >
                  <LogIn className="w-4 h-4" />
                  Sign In
                </NavLink>
              ))}
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 text-slate-600"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {menuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white">
            <nav className="flex flex-col px-4 py-3 gap-1">
              {navLinks.map(link => (
                <NavLink
                  key={link.href}
                  item={link}
                  onClick={() => setMenuOpen(false)}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    !link.external && location.pathname === link.href
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                />
              ))}
              {portalLoginEnabled && (
                <div className="border-t border-slate-100 mt-2 pt-2">
                  {isAuthenticated ? (
                    <NavLink
                      item={{ href: portalHomeUrl, label: 'My Portal', external: portalHomeUrl.startsWith('http') }}
                      onClick={() => setMenuOpen(false)}
                      className="block px-3 py-2.5 rounded-lg text-sm font-medium text-brand-700 bg-brand-50"
                    />
                  ) : (
                    <NavLink
                      item={{ href: loginUrl, label: 'Sign In', external: loginUrl.startsWith('http') }}
                      onClick={() => setMenuOpen(false)}
                      className="block px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-brand-700 text-center"
                    />
                  )}
                </div>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <main className="relative z-10 flex-1">
        <Outlet />
      </main>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="relative z-10 text-slate-400 py-10" style={portalTheme.footerSurface}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <TenantBrandMark
                brand={brand}
                size="sm"
                subtitle={brand.legalName}
                tone="inverse"
              />
              {experienceMode === 'portal' && storefrontBaseUrl && (
                <a
                  href={storefrontBaseUrl}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white transition-colors"
                >
                  Visit public website <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <p className="text-sm">
                {brand.footerBlurb}
              </p>
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold mb-3">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                {navLinks.map(link => (
                  <li key={link.href}>
                    {link.external ? (
                      <a href={link.href} className="hover:text-white transition-colors">
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.href} className="hover:text-white transition-colors">
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
                {portalLoginEnabled && (
                  <li>
                    {loginUrl.startsWith('http') ? (
                      <a href={loginUrl} className="hover:text-white transition-colors">
                        {brand.portalLabel}
                      </a>
                    ) : (
                      <Link to={loginUrl} className="hover:text-white transition-colors">
                        {brand.portalLabel}
                      </Link>
                    )}
                  </li>
                )}
              </ul>
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold mb-3">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 shrink-0" />
                  <span>{brand.primaryPhone || '—'}</span>
                </li>
                <li>{brand.supportEmail}</li>
                {brand.addressLines.map(line => <li key={line}>{line}</li>)}
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-slate-800 text-xs text-center">
            © {new Date().getFullYear()} {brand.legalName}. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
